import { useMutation } from '@apollo/client';
import styled from '@emotion/styled';
import type { Prismic } from '@landsbankinn/prismic';
import {
  Button,
  Column,
  device,
  FormDescription,
  FormTitle,
  Segment,
  Row,
  Form,
  theme,
} from '@landsbankinn/ui';
import { LandsbankinnContext } from '@landsbankinn/utils';
import { rgba } from 'polished';
import React, { useContext, useEffect, useState } from 'react';
import type { GdprUpdateType } from '../../../__generated__/globalTypes';
import type {
  UpdateGdprData as IUpdateGdprData,
  UpdateGdprDataVariables,
} from '../../graphql/gdpr/__generated__/updateGdprData';
import { UPDATE_GDPR_DATA } from '../../graphql/gdpr/updateGdprData';
import { PhoneAuthentication } from '../phone-authentication/PhoneAuthentication';
import { EmailGdpr } from './EmailGdpr';
import { GdprSuccess } from './GdprSuccess';
import { TextGdpr } from './TextGdpr';
import { useLogin } from './useLogin';

const Host = styled.div``;

const FormSpacer = styled.div<{ smallTop?: boolean }>`
  height: 1px;
  background: ${(props) => rgba(props.theme.colors.primary, 0.1)};
  ${(props) => (props.smallTop ? 'margin-top: 20px;' : 'margin-top: 40px;')};
  margin-bottom: 57px;
`;

const BigButton = styled(Button)`
  width: 100%;
`;

const NoPaddingSegment = styled(Segment)`
  &:last-of-type {
    padding-bottom: 57px;
  }
  background-color: ${(props) => props.theme.shades.primary2};
  margin-left: -44px;
  margin-right: -44px;
  @media ${device.tablet} {
    margin-left: initial;
    margin-right: initial;
  }
`;

const RowPadding = styled(Row)`
  margin-bottom: 20px;
`;

interface IProps {
  type: GdprUpdateType;
  title: string;
  descriptionText: string;
  login_title: string;
  login_description: string;
  login_image: Prismic.Image;
}

export function UpdateGdprData({
  type,
  login_title,
  login_description,
  login_image,
  title,
  descriptionText,
}: IProps) {
  const { client } = useContext(LandsbankinnContext);
  const [finished, setFinished] = useState(false);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [login, { data, loading, isLoggedIn, error }] = useLogin();
  const [update] = useMutation<IUpdateGdprData, UpdateGdprDataVariables>(
    UPDATE_GDPR_DATA,
    {
      client,
      context: {
        headers: { authorization: data?.token ? `Bearer ${data.token}` : '' },
      },
      onCompleted: () => setFinished(true),
    },
  );

  useEffect(() => {
    if (data?.user?.email) {
      setEmail(data.user.email);
    }
  }, [data?.user]);

  return (
    <Host>
      {!finished && !isLoggedIn && (
        <PhoneAuthentication
          title={login_title}
          description={login_description}
          image={login_image}
          login={login}
          loading={loading}
          error={error}
        />
      )}
      {!finished && isLoggedIn && (
        <NoPaddingSegment>
          <Form
            onSubmit={() => {
              update({
                variables: { data: { type, description, email } },
              } as any);
            }}
          >
            <RowPadding>
              <Column
                lg={{ width: 5 / 12 }}
                md={{ width: 12 / 12 }}
                sm={{ width: 12 / 12 }}
              >
                <FormTitle text={title} />
              </Column>
              <Column
                lg={{ offset: 1 / 12, width: 6 / 12 }}
                md={{ width: 12 / 12 }}
                sm={{ width: 12 / 12 }}
              >
                <FormDescription>{descriptionText}</FormDescription>
              </Column>
            </RowPadding>
            <Row>
              <Column
                lg={{ width: 6 / 12 }}
                md={{ width: 12 / 12 }}
                sm={{ width: 12 / 12 }}
              >
                <EmailGdpr onChange={setEmail} value={email as any} />
              </Column>
              <Column
                lg={{ width: 6 / 12 }}
                md={{ width: 12 / 12 }}
                sm={{ width: 12 / 12 }}
              >
                <TextGdpr
                  value={description as any}
                  onChange={setDescription}
                  type={type}
                />
              </Column>
            </Row>
            <FormSpacer />
            <Row>
              <Column
                lg={{ offset: 6 / 12, width: 6 / 12 }}
                md={{ width: 12 / 12 }}
                sm={{ width: 12 / 12 }}
              >
                <BigButton
                  bg={theme.colors.primary}
                  title="Senda beiðni"
                  type="submit"
                />
              </Column>
            </Row>
          </Form>
        </NoPaddingSegment>
      )}
      {finished && <GdprSuccess />}
    </Host>
  );
}
